import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { focusReason, focusTags, plural } from '../utils/focus';
import EmptyDayPanel from './EmptyDayPanel';
import DesktopDatePicker from './DesktopDatePicker';

// Время задачи «ЧЧ:ММ»; ровно 00:00 — «без времени», так понимают все клиенты.
function taskTime(task) {
  const m = /T(\d{2}):(\d{2})/.exec(task.due_date || '');
  return m && !(m[1] === '00' && m[2] === '00') ? `${m[1]}:${m[2]}` : '';
}

const RING_R = 27;
const RING_LEN = 2 * Math.PI * RING_R;

// Кольцо дня: сделано / (сделано + осталось). Пустой день — пустое кольцо, а не 0/0.
function DayRing({ done, left }) {
  const total = done + left;
  const share = total ? done / total : 0;
  return (
    <svg className="focus-ring" viewBox="0 0 66 66" aria-hidden="true">
      <circle cx="33" cy="33" r={RING_R} className="focus-ring-track" />
      <motion.circle
        cx="33" cy="33" r={RING_R}
        className="focus-ring-bar"
        strokeDasharray={RING_LEN}
        initial={false}
        animate={{ strokeDashoffset: RING_LEN * (1 - share) }}
        transition={{ type: 'spring', stiffness: 90, damping: 18 }}
        transform="rotate(-90 33 33)"
      />
      <text x="33" y="38" textAnchor="middle" className="focus-ring-text">
        {done}/{total}
      </text>
    </svg>
  );
}

function summaryLine(left, overdue, picked, done) {
  if (!left) return done ? 'Вы молодец — можно отдыхать' : 'Самое время выбрать';
  const parts = [];
  if (overdue) parts.push(`${overdue} ${plural(overdue, 'просрочена', 'просрочены', 'просрочено')}`);
  if (picked) parts.push(`${picked} ${plural(picked, 'главная', 'главные', 'главных')}`);
  return parts.length ? parts.join(' · ') : 'Ничего не горит';
}

export default function FocusPanel({
  task, today, doneCount, leftCount, overdueCount, pickedCount,
  onDone, onLater, onOpenDayClose, onOpenPick,
  emptyDay, streak = 0, triageCount = 0, onOpenTriage, staleCount = 0, onOpenStale, onOpenDetails,
  onDateChange, onRecurrence, formatRecurrence = r => r,
}) {
  // Время и повтор у карточки «Сейчас» (16.10.2026): раньше их можно было поставить только в списке.
  const whenRef = React.useRef(null);
  const [picker, setPicker] = React.useState(null); // null | 'date' | 'repeat'
  return (
    <section className="focus-panel" aria-label="Фокус на сегодня">
      <div className="focus-summary">
        <DayRing done={doneCount} left={leftCount} />
        <div className="focus-summary-text">
          <b>{leftCount ? `Осталось ${leftCount}` : doneCount ? 'День закрыт' : 'Пустой день'}</b>
          <span>{summaryLine(leftCount, overdueCount, pickedCount, doneCount)}</span>
          {streak >= 2 && (
            <span className="focus-streak">{streak} {plural(streak, 'день', 'дня', 'дней')} подряд с главным</span>
          )}
        </div>
        <div className="focus-summary-actions">
          <button type="button" className="focus-link" onClick={onOpenPick}>Главное</button>
          <button type="button" className="focus-link" onClick={onOpenDayClose}>Итог дня</button>
        </div>
      </div>

      {staleCount > 0 && (
        <div className="focus-banner">
          <span>{staleCount} {plural(staleCount, 'задача висит', 'задачи висят', 'задач висят')} дольше двух недель. Решим их судьбу?</span>
          <button type="button" className="focus-btn focus-btn-secondary" onClick={onOpenStale}>Разобрать</button>
        </div>
      )}
      {!staleCount && triageCount > 0 && (
        <div className="focus-banner">
          <span>Накопилось {triageCount} {plural(triageCount, 'просроченная', 'просроченные', 'просроченных')}. Разберём за минуту?</span>
          <button type="button" className="focus-btn focus-btn-secondary" onClick={onOpenTriage}>Разобрать</button>
        </div>
      )}
      {task && <div className="focus-label">Сейчас</div>}
      <AnimatePresence mode="wait" initial={false}>
        {task ? (
          <motion.article
            key={task.id}
            className="focus-hero"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -28, scale: 0.96, transition: { duration: 0.18, ease: 'easeIn' } }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          >
            <div className="focus-tags">
              {focusTags(task, today).map(tag => (
                <span key={tag} className={`focus-tag ${tag.startsWith('просроч') ? 'is-late' : ''}`}>{tag}</span>
              ))}
            </div>
            <h3 className="focus-title">{task.description}</h3>
            <p className="focus-reason">{focusReason(task, today)}</p>
            {onDateChange && (
              <div className="focus-when" ref={whenRef}>
                <button type="button" className={`focus-when-btn ${taskTime(task) ? 'is-set' : ''}`} onClick={() => setPicker('date')}>
                  🕐 {taskTime(task) || 'Время'}
                </button>
                {task.recurrence && (
                  <button type="button" className="recurrence-badge is-button" onClick={() => setPicker('repeat')}>
                    {formatRecurrence(task.recurrence)}
                  </button>
                )}
              </div>
            )}
            {picker && (
              <DesktopDatePicker
                anchorRef={whenRef}
                date={task.due_date ? task.due_date.slice(0, 10) : undefined}
                time={taskTime(task)}
                recurrence={task.recurrence}
                highlightRepeat={picker === 'repeat'}
                onRecurrence={code => onRecurrence && onRecurrence(task.id, code)}
                onSelect={dateStr => onDateChange(task.id, dateStr)}
                onBlur={() => setPicker(null)}
              />
            )}
            {task.details && (
              <button type="button" className="focus-details" onClick={() => onOpenDetails(task)}>
                <span className="focus-details-text">{task.details}</span>
              </button>
            )}
            <div className="focus-actions">
              <button type="button" className="focus-btn focus-btn-primary" onClick={() => onDone(task)}>
                ✓ Готово
              </button>
              <button type="button" className="focus-btn focus-btn-secondary" onClick={() => onLater(task)}>
                Позже
              </button>
            </div>
          </motion.article>
        ) : (
          <motion.div
            key="empty"
            className="empty-wrap"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <EmptyDayPanel doneCount={doneCount} {...emptyDay} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
